/*
 * Copyright 2026 Sony Corporation
 */

import { useEffect, useState } from 'react';

import { ApiError } from '../services/api-client';
import { useAuth } from '../contexts/auth-context';
import type { SketchfabAsset } from '@ips-device-verse/twin-service/schema/assets';

export interface UseSketchfabResult {
  assets: SketchfabAsset[];
  loading: boolean;
  error: string | undefined;
}

/** Long enough to mean "stopped typing" — the same restraint the Poly Haven tab shows (devplan §5.6). */
const SEARCH_IDLE_MS = 350;

const message = (cause: unknown): string => {
  if (cause instanceof ApiError) {
    return cause.status === 429 ? 'Sketchfab is busy. Try again in a moment.' : 'Sketchfab could not be reached.';
  }
  return 'Sketchfab could not be reached.';
};

/**
 * Sketchfab's catalogue, one page at a time.
 *
 * The query belongs to the caller: the Model tab searches both sources from
 * one box. Unlike Poly Haven there is no whole-catalogue response to filter in
 * the browser — it is millions of models — so every keystroke that settles is
 * a request, and an empty box is their most-liked models rather than nothing.
 */
export const useSketchfab = (query: string): UseSketchfabResult => {
  const { api } = useAuth();
  const [assets, setAssets] = useState<SketchfabAsset[]>();
  const [error, setError] = useState<string>();

  const trimmed = query.trim();

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(
      () => {
        setError(undefined);
        api.searchSketchfab(trimmed ? { q: trimmed } : {}, controller.signal).then(
          (page) => setAssets(page.assets),
          (cause: unknown) => {
            if (!controller.signal.aborted) {
              setAssets([]);
              setError(message(cause));
            }
          }
        );
      },
      // The first page should not wait for a pause that never came.
      trimmed.length > 0 ? SEARCH_IDLE_MS : 0
    );

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [api, trimmed]);

  return { assets: assets ?? [], loading: assets === undefined, error };
};
